/**
 * M03 intent → PB 映射矩阵 · S2 Day 7b
 *
 * 数据源：
 * - useIntentMapping → GET /admin/v1/playbook/intent_mapping（YAML 文本）
 * - usePutIntentMapping → PUT 同路径
 *
 * 设计：MVP 简化为 Monaco YAML 全文编辑（与 M02/M03 PB 编辑器同范式）
 *   - schema 单源后端 + 乐观锁 + 错误回显
 *   - 后端 unknown_intents warning 渲染（非阻塞 · 仅警示）
 *   - "矩阵表"形式作为只读视图：解析 yaml + 渲染 13×N 网格（让运营看清当前映射）
 *   - 编辑通过 Monaco YAML（直觉性更强 · 避免双向绑定复杂度）· S3 切真矩阵编辑
 */
import Editor, { type OnChange } from "@monaco-editor/react";
import { useEffect, useMemo, useState } from "react";

import { SnapshotPanel } from "@/components/shared/snapshot-panel";
import { useIntentMapping, usePlaybookList, usePutIntentMapping } from "@/lib/api/playbook";
import { intentLabel } from "@/lib/product-labels";

interface ApiErrorBody {
	detail?: string;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

const INTENT_LABELS = [
	"order_status_inquiry",
	"shipping_tracking",
	"order_issue",
	"refund_request",
	"return_request",
	"order_cancel",
	"address_change",
	"product_inquiry",
	"product_recommendation",
	"knowledge_qa",
	"discount_inquiry",
	"human_handoff",
	"unknown",
] as const;

/**
 * 粗略从 YAML 文本抽出 mappings: { intent: [pb_id, ...] }（仅作只读矩阵渲染）
 * 不依赖完整 YAML parser · 文本结构异常时返回空对象（用户从后端 schema 错误能得到反馈）
 */
function parseMappingPreview(yaml: string): Record<string, string[]> {
	const out: Record<string, string[]> = {};
	const mappingsMatch = yaml.match(/mappings:\s*\n([\s\S]+)$/);
	if (!mappingsMatch) return out;
	const lines = (mappingsMatch[1] ?? "").split("\n");
	for (const line of lines) {
		const m = /^\s*([a-z_]+):\s*\[([^\]]*)\]\s*$/i.exec(line);
		if (!m) continue;
		const intent = m[1] ?? "";
		const list = (m[2] ?? "")
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
		out[intent] = list;
	}
	return out;
}

export function IntentMappingMatrix() {
	const { data, isLoading, isError, error, refetch } = useIntentMapping();
	const listQuery = usePlaybookList();
	const putMutation = usePutIntentMapping();

	const [draft, setDraft] = useState<string>("");
	const [loadedVersion, setLoadedVersion] = useState<string | null>(null);
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [conflictMsg, setConflictMsg] = useState<string | null>(null);
	const [unknownWarn, setUnknownWarn] = useState<string[]>([]);

	useEffect(() => {
		if (data) {
			setDraft(data.yaml_text);
			setLoadedVersion(data.bundle_version);
			setSavedAt(null);
			setConflictMsg(null);
		}
	}, [data]);

	const isDirty = data ? draft !== data.yaml_text : false;
	const previewMappings = useMemo(() => parseMappingPreview(draft), [draft]);
	const knownPbIds = new Set(listQuery.data?.items.map((p) => p.playbook_id) ?? []);

	const handleSave = () => {
		setConflictMsg(null);
		setSavedAt(null);
		setUnknownWarn([]);
		putMutation.mutate(
			{ yaml_text: draft, expected_version: loadedVersion },
			{
				onSuccess: (res) => {
					setSavedAt(new Date().toLocaleTimeString());
					setLoadedVersion(res.bundle_version);
					setUnknownWarn(res.unknown_intents);
				},
				onError: (err) => {
					const detail = extractErrorDetail(err);
					const status = (err as { response?: { status?: number } })?.response?.status;
					if (status === 409) {
						setConflictMsg(`乐观锁冲突 · 后端版本已变 · 请重载。原始错误：${detail}`);
					} else {
						setConflictMsg(`保存失败：${detail}`);
					}
				},
			},
		);
	};

	const handleReload = async () => {
		const result = await refetch();
		if (result.data) {
			setDraft(result.data.yaml_text);
			setLoadedVersion(result.data.bundle_version);
			setConflictMsg(null);
		}
	};

	const handleEditorChange: OnChange = (value) => setDraft(value ?? "");

	if (isLoading) {
		return (
			<div data-testid="mapping-loading" className="p-4 text-sm text-text-secondary">
				加载 mapping YAML 中...
			</div>
		);
	}
	if (isError) {
		return (
			<div data-testid="mapping-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	return (
		<div className="space-y-3" data-testid="mapping-matrix">
			<SnapshotPanel
				module={{ kind: "intent_mapping" }}
				moduleLabel="意图→方案 mapping"
				testidPrefix="mapping-snapshot"
			/>
			<div className="flex flex-wrap items-center justify-between gap-2 text-sm">
				<div className="text-text-secondary">
					当前版本 <span className="font-mono text-xs">{loadedVersion ?? "—"}</span>
					{isDirty ? (
						<span className="ml-2 rounded border border-brand-warning/30 bg-brand-warning/10 px-2 py-0.5 text-xs text-brand-warning">
							草稿未保存
						</span>
					) : null}
					{savedAt ? (
						<span
							data-testid="mapping-saved-at"
							className="ml-2 rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
						>
							已保存 {savedAt}
						</span>
					) : null}
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						className="rounded-md border border-surface-border px-3 py-1.5 text-sm hover:bg-surface-subtle"
						data-testid="mapping-reload"
						onClick={handleReload}
					>
						重载最新版本
					</button>
					<button
						type="button"
						className="rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
						data-testid="mapping-save"
						onClick={handleSave}
						disabled={putMutation.isPending || !isDirty}
					>
						{putMutation.isPending ? "保存中..." : "保存"}
					</button>
				</div>
			</div>

			<div className="grid gap-3 lg:grid-cols-2">
				<div className="rounded-md border border-surface-border" data-testid="mapping-monaco">
					<div className="border-b border-surface-border bg-surface-subtle px-3 py-1.5 text-xs text-text-secondary">
						配置编辑（以后端校验为准）
					</div>
					<Editor
						height="50vh"
						language="yaml"
						value={draft}
						onChange={handleEditorChange}
						options={{
							minimap: { enabled: false },
							fontSize: 13,
							tabSize: 2,
							wordWrap: "on",
							scrollBeyondLastLine: false,
						}}
					/>
				</div>

				<div className="rounded-md border border-surface-border" data-testid="mapping-preview">
					<div className="border-b border-surface-border bg-surface-subtle px-3 py-2 text-xs leading-5 text-text-secondary">
						<div>兜底映射预览</div>
						<div>RoutingTable 未命中时才读取这里；主路由规则在“路由规则”维护。</div>
					</div>
					<div className="overflow-auto p-2">
						<table className="w-full border-collapse text-xs">
							<thead>
								<tr className="text-left">
									<th className="px-2 py-1 font-medium">分类</th>
									<th className="px-2 py-1 font-medium">处理方案</th>
								</tr>
							</thead>
							<tbody>
								{INTENT_LABELS.map((intent) => {
									const pbs = previewMappings[intent] ?? [];
									return (
										<tr
											key={intent}
											data-testid={`mapping-row-${intent}`}
											className="border-t border-surface-border"
										>
											<td className="px-2 py-1">
												<div className="font-medium text-text-primary">{intentLabel(intent)}</div>
											</td>
											<td className="px-2 py-1">
												{pbs.length === 0 ? (
													<span className="text-text-muted">
														{intent === "order_issue"
															? "由 RoutingTable 路由"
															: "—（走知识检索或转人工）"}
													</span>
												) : (
													<div className="flex flex-wrap gap-1">
														{pbs.map((pb) => {
															const known = knownPbIds.has(pb);
															return (
																<span
																	key={pb}
																	className={`rounded-full px-1.5 py-0.5 font-mono ${
																		known
																			? "bg-brand-success/10 text-brand-success"
																			: "bg-brand-warning/10 text-brand-warning"
																	}`}
																>
																	{pb}
																	{known ? null : " ⚠️"}
																</span>
															);
														})}
													</div>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{unknownWarn.length > 0 ? (
				<div
					data-testid="mapping-unknown-warning"
					className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-xs text-brand-warning"
				>
					未知分类提示（不阻塞写入）：{unknownWarn.join(", ")}
				</div>
			) : null}

			{conflictMsg ? (
				<div
					data-testid="mapping-conflict"
					className="rounded-md border border-brand-danger bg-brand-danger/10 p-3 text-sm text-brand-danger"
				>
					{conflictMsg}
				</div>
			) : null}

			<p className="text-xs text-text-muted">
				当前先用配置文本编辑和矩阵预览保证关系清楚；后续再评估是否做表单化编辑。
			</p>
		</div>
	);
}
